import { approvalChallengeFor } from './approval-batch.js';
import { fromBase64Url, randomBytes, toBase64Url } from './bytes.js';
import { CHECKOUT_STEP_UP_KINDS } from './checkout.js';
import { DOMAIN, domainSeparated, hashCanonical, hashCanonicalB64 } from './hash.js';
import { signP256, verifyP256 } from './keys.js';
import { LAND_GRANT_STEP_UP_KINDS } from './land-grant.js';
/** Command kinds that always require a passkey step-up. `x.*` matches the `x.` prefix. */
export const DEFAULT_RISKY_KINDS = [
    'land.merge',
    'land.deploy',
    'release.*',
    'secret.*',
    'policy.*',
    'device.*',
    'credential.*',
    'daemon.break-glass',
    'daemon.break-glass.*',
    'work.delete-unmerged',
    // account-projects: cloning/creating a repo (statement-approved) and machine-local policy.
    'project.checkout',
    'project.policy',
    // phase2-decisions: answering a secrets/security/release/irreversible decision.
    'decision.answer.risky',
    // live-preview-tunnel: letting previews reach a port the run does not own (writes to the host).
    'preview.share_port',
];
export const DEFAULT_COMMAND_TTL_MS = 2 * 60_000;
/** Normal commands may be queued for an offline daemon (e.g. a sleeping PC) for up to a day. */
export const MAX_COMMAND_TTL_MS = 24 * 60 * 60_000;
/** Risky commands (and their step-up tap) must be used within minutes. */
export const MAX_RISKY_COMMAND_TTL_MS = 5 * 60_000;
export const COMMAND_CLOCK_SKEW_MS = 30_000;
export function isRiskyKind(kind, patterns = DEFAULT_RISKY_KINDS) {
    return patterns.some((p) => (p.endsWith('.*') ? kind.startsWith(p.slice(0, -1)) : kind === p));
}
export async function hashPayload(payload) {
    return hashCanonicalB64(DOMAIN.payload, payload);
}
export async function createCommandEnvelope(input) {
    const issuedAt = input.now ?? Date.now();
    return {
        v: 1,
        id: input.id ?? globalThis.crypto.randomUUID(),
        machineId: input.machineId,
        kind: input.kind,
        payloadHash: await hashPayload(input.payload),
        nonce: input.nonce ?? toBase64Url(randomBytes(16)),
        issuedAt,
        expiresAt: issuedAt + (input.ttlMs ?? DEFAULT_COMMAND_TTL_MS),
        deviceKeyId: input.deviceKeyId,
        riskLevel: input.riskLevel ?? (isRiskyKind(input.kind, input.riskyKinds) ? 'risky' : 'normal'),
    };
}
/** Bytes the device key signs. */
export function commandSigningInput(envelope) {
    return domainSeparated(DOMAIN.command, envelope);
}
/** WebAuthn challenge for a step-up: binds the tap to this exact envelope (and so its payload). */
export async function createStepUpChallenge(envelope) {
    return hashCanonical(DOMAIN.stepUp, envelope);
}
/** Build, hash and sign a command. Attach `stepUp` afterwards for risky kinds. */
export async function signCommand(input, device, options = {}) {
    const envelope = await createCommandEnvelope({ ...input, deviceKeyId: device.deviceKeyId });
    return signEnvelope(envelope, input.payload, device.privateKey, options);
}
export async function signEnvelope(envelope, payload, privateKey, options = {}) {
    const signature = toBase64Url(await signP256(privateKey, commandSigningInput(envelope)));
    const signed = { envelope, signature };
    if (options.includePayload ?? true)
        signed.payload = payload;
    if (options.endorsement)
        signed.endorsement = options.endorsement;
    return signed;
}
function reject(reason, detail) {
    return detail === undefined ? { ok: false, reason } : { ok: false, reason, detail };
}
function isEnvelopeShape(e) {
    if (typeof e !== 'object' || e === null)
        return false;
    const x = e;
    const str = (k) => typeof x[k] === 'string' && x[k].length > 0;
    if (!str('id') ||
        !str('machineId') ||
        !str('kind') ||
        !str('payloadHash') ||
        !str('nonce') ||
        !str('deviceKeyId') ||
        !Number.isSafeInteger(x.issuedAt) ||
        !Number.isSafeInteger(x.expiresAt) ||
        (x.riskLevel !== 'normal' && x.riskLevel !== 'risky')) {
        return false;
    }
    try {
        return fromBase64Url(x.nonce).length >= 16;
    }
    catch {
        return false;
    }
}
/**
 * Daemon-side verification. Checks, in order: shape and version, target machine, device
 * endorsement and revocation, device signature, payload hash, validity window, step-up, and
 * finally records the nonce (so only fully valid commands consume one).
 */
export async function verifyCommand(signed, options) {
    const r = await verifyCommandCore(signed, options, false);
    return 'statementPending' in r ? reject('missing-step-up') : r;
}
/**
 * Whether a kind may carry a statement approval instead of a step-up: a checkout statement, or
 * (land-grant) a standing landing grant for `land.merge`. Never releases, secrets or deletions.
 */
export function acceptsStatementStepUp(kind) {
    return CHECKOUT_STEP_UP_KINDS.includes(kind) || LAND_GRANT_STEP_UP_KINDS.includes(kind);
}
/**
 * `deferStatement`: the payload is still encrypted, so a statement step-up is checked by the
 * caller after decryption (`statementPending`). Only possible when a hook is configured.
 */
export async function verifyCommandCore(signed, options, deferStatement) {
    const now = options.now ?? Date.now();
    const skew = options.clockSkewMs ?? COMMAND_CLOCK_SKEW_MS;
    const { trust, nonceCache } = options;
    const envelope = signed?.envelope;
    if (typeof envelope !== 'object' || envelope === null)
        return reject('malformed');
    if (envelope.v !== 1)
        return reject('unsupported-version');
    if (!isEnvelopeShape(envelope) || typeof signed.signature !== 'string')
        return reject('malformed');
    if (envelope.machineId !== options.machineId) {
        return reject('machine-mismatch', `${envelope.machineId} != ${options.machineId}`);
    }
    // Device: known + endorsed + not revoked. An inline endorsement can introduce a new device.
    let lookup = trust.lookupDevice(envelope.deviceKeyId, now);
    if (lookup.status === 'unknown' || lookup.status === 'expired') {
        if (signed.endorsement !== undefined) {
            if (signed.endorsement?.statement?.deviceKeyId !== envelope.deviceKeyId) {
                return reject('bad-endorsement', 'endorsement is for a different device');
            }
            const added = await trust.addEndorsement(signed.endorsement, now);
            if (!added.ok) {
                if (added.reason === 'revoked-device' || added.reason === 'revoked-credential') {
                    return reject('revoked', added.reason);
                }
                return reject('bad-endorsement', added.reason);
            }
            lookup = trust.lookupDevice(envelope.deviceKeyId, now);
        }
    }
    if (lookup.status === 'revoked')
        return reject('revoked', lookup.detail);
    if (lookup.status === 'expired')
        return reject('unknown-device', 'endorsement expired');
    if (lookup.status !== 'ok')
        return reject('unknown-device');
    const device = lookup.device;
    let signature;
    try {
        signature = fromBase64Url(signed.signature);
    }
    catch {
        return reject('bad-signature', 'signature encoding');
    }
    if (!(await verifyP256(device.publicKey, signature, commandSigningInput(envelope)))) {
        return reject('bad-signature');
    }
    const payload = options.payload !== undefined ? options.payload : signed.payload;
    if (payload === undefined)
        return reject('malformed', 'missing payload');
    let payloadHash;
    try {
        payloadHash = await hashPayload(payload);
    }
    catch {
        return reject('malformed', 'payload is not canonicalizable');
    }
    if (payloadHash !== envelope.payloadHash)
        return reject('payload-mismatch');
    if (envelope.expiresAt <= envelope.issuedAt)
        return reject('malformed', 'expiresAt <= issuedAt');
    const stepUpRequired = envelope.riskLevel === 'risky' || isRiskyKind(envelope.kind, options.riskyKinds);
    const maxTtl = stepUpRequired
        ? (options.maxRiskyTtlMs ?? MAX_RISKY_COMMAND_TTL_MS)
        : (options.maxTtlMs ?? MAX_COMMAND_TTL_MS);
    if (envelope.expiresAt - envelope.issuedAt > maxTtl)
        return reject('ttl-too-long');
    if (envelope.issuedAt > now + skew)
        return reject('not-yet-valid');
    if (now > envelope.expiresAt + skew)
        return reject('expired');
    const nonceKey = `${envelope.deviceKeyId}:${envelope.nonce}`;
    if (nonceCache.has(nonceKey, now))
        return reject('replay');
    let stepUpCredentialId;
    let stepUpGrantId;
    let statementPending = false;
    if (signed.stepUp !== undefined) {
        let challenge;
        try {
            challenge = await approvalChallengeFor(DOMAIN.stepUp, await createStepUpChallenge(envelope), signed.stepUpBatch);
        }
        catch {
            return reject('bad-step-up', 'step-up batch');
        }
        const approval = await trust.verifyPasskeyApproval(signed.stepUp, challenge, {
            trackSignCount: true,
            batch: signed.stepUpBatch !== undefined,
        });
        if (!approval.ok)
            return reject('bad-step-up', approval.reason);
        stepUpCredentialId = approval.credential.credentialId;
    }
    else if (stepUpRequired) {
        if (!options.statementStepUp || !acceptsStatementStepUp(envelope.kind))
            return reject('missing-step-up', envelope.kind);
        if (deferStatement)
            statementPending = true;
        else {
            const approved = await options.statementStepUp(envelope, payload);
            if (!approved.ok)
                return reject('bad-step-up', approved.detail ?? 'statement');
            stepUpCredentialId = approved.credentialId;
            if (approved.grantId)
                stepUpGrantId = approved.grantId;
        }
    }
    const check = nonceCache.checkAndAdd(nonceKey, envelope.expiresAt + skew, now);
    if (check === 'replay')
        return reject('replay');
    if (check === 'full')
        return reject('replay', 'nonce cache full; refusing (fail closed)');
    if (statementPending)
        return { ok: true, envelope, payload, device, statementPending: true };
    if (stepUpCredentialId === undefined)
        return { ok: true, envelope, payload, device };
    return stepUpGrantId === undefined
        ? { ok: true, envelope, payload, device, stepUpCredentialId }
        : { ok: true, envelope, payload, device, stepUpCredentialId, stepUpGrantId };
}
//# sourceMappingURL=command.js.map